// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Styled, Space, Icon, ScrollView, ReorderingDragAndDrop} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  usePopover,
  useToast,
  useModal,
  useNavigation,
  useResponsive,
  ResponsiveType,
} from '@supermove/hooks';
import {StageModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {ViewStyleProp} from '@supermove/styles/types';
import {List} from '@supermove/utils';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import ResponsiveBadge from '@shared/design/components/Badge/ResponsiveBadge';
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import IconButton from '@shared/design/components/IconButton';
import ConfirmationModal from '@shared/design/components/Modal/SmallModal/ConfirmationModal';
import WarningModal from '@shared/design/components/Modal/SmallModal/WarningModal';
import Text from '@shared/design/components/Text';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import StageProjectStatus from '@shared/modules/Lead/enums/StageProjectStatus';
import ReorderStagesForm from '@shared/modules/Project/forms/ReorderStagesForm';
import ToggleStageIsArchivedForm from '@shared/modules/Project/forms/ToggleStageIsArchivedForm';
import useReorderStagesMutation from '@shared/modules/Project/hooks/useReorderStagesMutation';
import useToggleStageIsArchivedMutation from '@shared/modules/Project/hooks/useToggleStageIsArchivedMutation';

const BadgeContainer = Styled.View`
  align-items: center;
  min-width: 240px;
`;

const ButtonContainer = Styled.View`
  width: 100%;
`;

const Container = Styled.View<{responsive: ResponsiveType}>`
  ${({responsive}) => !responsive.desktop && 'padding: 0px 16px;'}
`;

const RowContainer = Styled.View`
  flex-direction: row;
`;

const StageBadgeContainer = Styled.View<{color: string}>`
  flex-direction: row;
  justify-content: space-between;
  width: 240px;
  padding: 12px;
  border-radius: 4px;
  background-color: ${({color}) => `${color}19`};
`;

const Stage = ({
  stage,
  handleView,
  refetch,
}: {
  stage: StageModel;
  handleView: (stageUuid: string) => void;
  refetch: () => void;
}) => {
  const officeStaffActionsPopover = usePopover();
  const archiveStageModal = useModal({name: 'Archive Lead Stage Modal'});
  const restoreStageModal = useModal({name: 'Restore Lead Stage Modal'});
  const handleToggleArchivedSuccessToast = useToast({ToastComponent: SuccessToast});

  const {handleSubmit: handleToggleArchived} = useToggleStageIsArchivedMutation({
    form: ToggleStageIsArchivedForm.new({
      stageId: Number(stage.id),
      isArchived: !stage.archivedAt,
    }),
    onSuccess: ({stage: updatedStage}: {stage: StageModel}) => {
      const message = updatedStage.archivedAt ? 'Lead stage archived.' : 'Lead stage restored.';
      handleToggleArchivedSuccessToast.handleToast({message});
      officeStaffActionsPopover.handleClose();
      archiveStageModal.handleClose();
      refetch();
    },
    onError: (errors) => {
      console.error('Error toggling lead stage archive status:', errors);
    },
  });

  const isArchived = !!stage.archivedAt;

  return (
    <StageBadgeContainer color={stage.color}>
      <ResponsiveBadge textColor={colors.white} backgroundColor={stage.color} label={stage.name} />
      <ActionMenuPopover
        popover={officeStaffActionsPopover}
        width={160}
        actions={[
          {
            text: 'View lead stage',
            onPress: () => {
              handleView(stage.uuid);
              officeStaffActionsPopover.handleClose();
            },
          },
          {
            text: isArchived ? 'Restore' : 'Archive',
            color: isArchived ? colors.gray.primary : colors.red.warning,
            onPress: () => {
              if (isArchived) {
                restoreStageModal.handleOpen();
              } else {
                archiveStageModal.handleOpen();
              }
            },
          },
        ]}
      >
        <IconButton
          source={Icon.EllipsisV}
          onPress={officeStaffActionsPopover.handleOpen}
          isSecondary
        />
      </ActionMenuPopover>
      <WarningModal
        isOpen={archiveStageModal.isOpen}
        title={`Archive '${stage.name}'?`}
        message={`New leads won't be assigned to this lead stage.`}
        handlePressOutside={archiveStageModal.handleClose}
        primaryActionText={'Confirm'}
        handlePrimaryAction={handleToggleArchived}
        secondaryActionText={'Cancel'}
        handleSecondaryAction={archiveStageModal.handleClose}
      />
      <ConfirmationModal
        icon={Icon.CirclePlus}
        isOpen={restoreStageModal.isOpen}
        title={`Restore '${stage.name}'?`}
        subtitle={`New leads can be assigned to this lead stage, but existing leads will remain unchanged.`}
        handlePressOutside={restoreStageModal.handleClose}
        primaryActionText={'Confirm'}
        handlePrimaryAction={handleToggleArchived}
        secondaryActionText={'Cancel'}
        handleSecondaryAction={restoreStageModal.handleClose}
      />
    </StageBadgeContainer>
  );
};

const StagesList = ({
  stages,
  status,
  handleView,
  refetch,
}: {
  stages: StageModel[];
  status: string;
  handleView: (stageUuid: string) => void;
  refetch: () => void;
}) => {
  const filteredStages = stages.filter((stage) => stage.projectStatus === status);
  const reorderStagesForm = ReorderStagesForm.edit(filteredStages);
  const {form, handleSubmit} = useReorderStagesMutation({
    reorderStagesForm,
    onSuccess: () => {},
    onError: (errors) => console.error({errors}),
  });

  return (
    <ReorderingDragAndDrop
      items={form.values.reorderStagesForm.stageForms}
      itemIdExtractor={'stageId'}
      renderItem={(stageForm) => {
        const stage = filteredStages.find((stage) => stage.id === stageForm.stageId);
        // When a lead stage is archived, there is a moment before the
        // form is reinitialized when there is a lead stage form but no
        // corresponding lead stage for it.
        if (!stage) {
          return null;
        }
        return <Stage stage={stage} handleView={handleView} refetch={refetch} />;
      }}
      spaceBetweenItems={8}
      handleReorder={({fromIndex, toIndex}) => {
        const stageForms = [...form.values.reorderStagesForm.stageForms];
        const reorderedStageForms = List.move({
          list: stageForms,
          fromIndex,
          toIndex,
        });
        form.setFieldValue('reorderStagesForm.stageForms', reorderedStageForms);
        setTimeout(handleSubmit, 0);
      }}
      isHorizontal
    />
  );
};

interface StageContentProps {
  stages: StageModel[];
  status: string;
  handleAdd: () => void;
  handleView: (stageUuid: string) => void;
  refetch: () => void;
}

const StageContent = ({status, stages, handleAdd, handleView, refetch}: StageContentProps) => {
  const {params} = useNavigation();
  const filteredStages = stages.filter((stage) => stage.projectStatus === status);
  return (
    <React.Fragment>
      <BadgeContainer>
        <ResponsiveBadge
          textColor={colors.gray.primary}
          backgroundColor={colors.getBackgroundColor(colors.gray.primary)}
          label={_.startCase(_.toLower(status))}
          isWidthOfContainer
        />
        {!params.isArchived && (
          <React.Fragment>
            <Space height={8} />
            <ButtonContainer>
              <SecondaryButton
                text='New Lead Stage'
                iconLeft={Icon.Plus}
                isWidthOfContainer
                onPress={handleAdd}
              />
            </ButtonContainer>
          </React.Fragment>
        )}
        <Space height={8} />
        <RowContainer>
          {params.isArchived && !filteredStages.length ? (
            <React.Fragment>
              <Text.Body style={{color: colors.gray.secondary}}>
                No archived stages for this status.
              </Text.Body>
            </React.Fragment>
          ) : (
            <StagesList
              stages={filteredStages}
              status={status}
              handleView={handleView}
              refetch={refetch}
            />
          )}
        </RowContainer>
      </BadgeContainer>
      <Space width={24} />
    </React.Fragment>
  );
};

interface SettingsProjectStagesProps {
  stages: StageModel[];
  handleAdd: () => void;
  handleView: (stageUuid: string) => void;
  refetch: () => void;
  scrollViewStyle?: ViewStyleProp;
}

const SettingsProjectStages = ({
  stages,
  handleAdd,
  handleView,
  refetch,
  scrollViewStyle,
}: SettingsProjectStagesProps) => {
  const responsive = useResponsive();
  return (
    <Container responsive={responsive}>
      <ScrollView horizontal style={scrollViewStyle}>
        {StageProjectStatus.STATUSES.map((status, index) => (
          <StageContent
            key={status}
            status={status}
            stages={stages}
            handleAdd={handleAdd}
            handleView={handleView}
            refetch={refetch}
          />
        ))}
      </ScrollView>
    </Container>
  );
};

SettingsProjectStages.fragment = gql`
  ${ReorderStagesForm.edit.fragment}

  fragment SettingsProjectStages on Stage {
    id
    name
    color
    uuid
    projectStatus
    archivedAt
    ...ReorderStagesForm_edit
  }
`;

export default SettingsProjectStages;
